import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, Loader2, UserPlus, Building2, ShieldAlert, Check } from "lucide-react";
import { ROLES, PLANS } from "./constants";

// Modal that provisions a new user. Supports two org-assignment modes:
//   - "existing": pick from the dropdown of organizations the admin already has
//   - "new":      create a brand new organization on the fly using its name
// Admins (role === "admin") never belong to an organization, so the entire
// org-picker section hides when that role is selected.
export function AddUserModal({ open, onOpenChange, onCreated, organizations, onOrgCreated }) {
  const [form, setForm] = useState({ email: "", name: "", company: "", role: "client_user", organization_id: "", organization_name: "", organization_plan: "starter" });
  const [submitting, setSubmitting] = useState(false);
  const [orgMode, setOrgMode] = useState("existing");

  useEffect(() => {
    if (!open) {
      setForm({ email: "", name: "", company: "", role: "client_user", organization_id: "", organization_name: "", organization_plan: "starter" });
      setOrgMode(organizations.length > 0 ? "existing" : "new");
    }
  }, [open, organizations.length]);

  // Resolve the currently-selected org name for the live confirmation banner.
  const selectedOrgName =
    form.role === "admin"
      ? null
      : orgMode === "existing"
        ? (organizations.find((o) => o.id === form.organization_id)?.name || "")
        : form.organization_name.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { email: form.email, name: form.name, company: form.company, role: form.role };
    if (form.role !== "admin") {
      if (orgMode === "existing") {
        if (!form.organization_id) { toast.error("Please select an organization."); return; }
        payload.organization_id = form.organization_id;
      } else {
        if (!form.organization_name.trim()) { toast.error("Please enter an organization name."); return; }
        payload.organization_name = form.organization_name.trim();
        payload.organization_plan = form.organization_plan || "starter";
      }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/users", payload);
      toast.success(`Account created for ${data.user.email}`);
      onCreated(data);
      if (orgMode === "new" && onOrgCreated) onOrgCreated();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create user.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="add-user-modal">
        <DialogHeader>
          <DialogTitle>Add a new client</DialogTitle>
          <DialogDescription>
            Provision an account. A temporary password is generated automatically — you'll see it on the next screen.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3" data-testid="add-user-form">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 rounded-lg mt-1.5" data-testid="add-user-name" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 rounded-lg mt-1.5" data-testid="add-user-email" />
          </div>
          <div>
            <Label htmlFor="role">Role</Label>
            <select
              id="role" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm mt-1.5"
              data-testid="add-user-role"
            >
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label} — {r.description}</option>)}
            </select>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Admins manage all organizations and don't belong to one.
            </p>
          </div>

          {form.role !== "admin" ? (
            <div className="rounded-lg border-2 border-teal-500 bg-teal-50/60 p-3.5 space-y-2.5" data-testid="add-user-org-section">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-teal-700" />
                <div className="text-sm font-semibold text-teal-900 uppercase tracking-wider">Organization <span className="text-red-600">*</span></div>
              </div>
              <p className="text-[11px] text-slate-600 -mt-1">
                This user will only ever see data uploaded inside the organization you pick below.
              </p>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={() => setOrgMode("existing")}
                  disabled={organizations.length === 0}
                  className={`px-2.5 py-1 rounded-md font-semibold transition-colors ${
                    orgMode === "existing"
                      ? "bg-teal-600 text-white"
                      : organizations.length === 0 ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-white border border-slate-300 text-slate-700"
                  }`}
                  data-testid="add-user-org-existing-tab"
                >Existing</button>
                <button type="button" onClick={() => setOrgMode("new")}
                  className={`px-2.5 py-1 rounded-md font-semibold transition-colors ${
                    orgMode === "new" ? "bg-teal-600 text-white" : "bg-white border border-slate-300 text-slate-700"
                  }`}
                  data-testid="add-user-org-new-tab"
                >New org</button>
              </div>
              {orgMode === "existing" ? (
                <select
                  value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm bg-white"
                  data-testid="add-user-org-select"
                  required
                >
                  <option value="">Select an organization...</option>
                  {organizations.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.user_count} {o.user_count === 1 ? "user" : "users"})</option>)}
                </select>
              ) : (
                <>
                  <Input
                    value={form.organization_name}
                    onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                    placeholder="e.g., St Mary's Hospital"
                    className="h-10 rounded-lg bg-white"
                    data-testid="add-user-org-name"
                    required
                  />
                  {/* Plan picker — only shown for new orgs. Existing orgs already have a plan. */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-teal-900 uppercase tracking-wider">
                      <Sparkles className="h-3 w-3" />
                      Plan for this new organization
                    </div>
                    <select
                      value={form.organization_plan}
                      onChange={(e) => setForm({ ...form, organization_plan: e.target.value })}
                      className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm bg-white font-semibold"
                      data-testid="add-user-org-plan"
                    >
                      {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    {(() => {
                      const selected = PLANS.find((p) => p.value === form.organization_plan);
                      return selected ? (
                        <p className="text-[10px] text-slate-600 leading-snug">{selected.sublabel}</p>
                      ) : null;
                    })()}
                  </div>
                </>
              )}
              {selectedOrgName ? (
                <div className="rounded-md bg-teal-600 text-white px-3 py-2 text-xs flex items-center gap-2" data-testid="add-user-org-confirmation">
                  <Check className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">{form.name || "This user"}</span> will be added to{" "}
                    <span className="font-semibold underline" data-testid="add-user-org-confirmation-name">{selectedOrgName}</span>
                  </span>
                </div>
              ) : (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-center gap-2">
                  <ShieldAlert className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{orgMode === "existing" ? "Pick an organization above before continuing." : "Type the new organization's name above."}</span>
                </div>
              )}
            </div>
          ) : null}

          <div>
            <Label htmlFor="company">Job title / dept (optional)</Label>
            <Input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="CFO, Biller, Revenue Cycle Lead..." className="h-10 rounded-lg mt-1.5" data-testid="add-user-company" />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting || (form.role !== "admin" && !selectedOrgName)} className="ai-cta rounded-lg" data-testid="add-user-submit">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Create account
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
